import { useEffect, useRef, type ReactNode } from 'react';
import { Code2, ExternalLink, GraduationCap, HeartHandshake, Mail, MapPin, Presentation, Scale, Users, type LucideIcon } from 'lucide-react';
import { InstitutionLogo } from '../components/common/InstitutionLogo';

const topics = [
  { id: 'authors', title: 'Developers & authors', icon: Users },
  { id: 'contact', title: 'Contact & collaboration', icon: Mail },
  { id: 'research', title: 'Research & presentations', icon: Presentation },
  { id: 'technology', title: 'Built with', icon: Code2 },
  { id: 'acknowledgment', title: 'Acknowledgment', icon: HeartHandshake },
  { id: 'license', title: 'License & credits', icon: Scale },
];

const technologies = [
  ['Rocq & jsCoq', 'The proof assistant runs in the browser through jsCoq and its WebAssembly backend.'],
  ['React & TypeScript', 'The application interface connects worlds, exercises, progress, and proof feedback.'],
  ['CodeMirror', 'The interactive editor supports Rocq code, command-by-command execution, and syntax highlighting.'],
  ['Vite', 'The development server and build tools prepare the application for deployment.'],
  ['Tailwind CSS', 'Responsive layouts and light and dark themes support different screens and preferences.'],
  ['Rocq world DSL', 'Annotated .v files define learning content and are converted into JSON worlds for the application.'],
];

function AboutSection({ id, title, icon: Icon, children }: {
  id: string;
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-24 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800 sm:p-8">
      <h2 id={`${id}-title`} className="mb-5 flex items-center gap-3 text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
        <Icon size={24} className="shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        {title}
      </h2>
      <div className="space-y-4 leading-relaxed text-gray-600 dark:text-gray-300">{children}</div>
    </section>
  );
}

export function AboutPage() {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:py-12">
      <div className="mb-10 max-w-3xl">
        <h1 ref={headingRef} tabIndex={-1} className="mb-4 text-3xl font-bold text-gray-900 outline-none dark:text-gray-100 sm:text-4xl">About Rocq Game</h1>
        <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
          An interactive learning environment for the Rocq proof assistant, type theory, logic,
          semantics, and formal reasoning. Write definitions, explore computations, and build
          machine-checked proofs directly in your browser.
        </p>
        <p className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <GraduationCap size={20} className="shrink-0" aria-hidden="true" />
          Developed at the Technical University of Košice, Slovakia.
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
        <nav aria-label="About topics" className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:sticky lg:top-24">
          <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">On this page</p>
          <ul className="flex flex-wrap gap-1 lg:flex-col">
            {topics.map(({ id, title, icon: Icon }) => (
              <li key={id}>
                <a href={`#${id}`} className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-primary-300">
                  <Icon size={16} className="shrink-0" aria-hidden="true" />{title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 space-y-6">
          <AboutSection id="authors" title="Developers & authors" icon={Users}>
            <div className="grid gap-4 sm:grid-cols-2">
              {['Ján Perháč', 'Khaled Suliman'].map(name => (
                <div key={name} className="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{name}</h3>
                  <p className="mt-1 text-sm">Technical University of Košice</p>
                </div>
              ))}
            </div>
          </AboutSection>

          <AboutSection id="contact" title="Contact & collaboration" icon={Mail}>
            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <div className="rounded-lg bg-gray-50 p-5 dark:bg-gray-900/50">
                <p className="text-sm">Would you like to use Rocq Game in a course you teach at your university? Whether you want to adapt existing materials for your students, create new learning worlds, or explore research collaboration, we would be glad to hear from you. Contact Ján Perháč to discuss your ideas.</p>
                <div className="mt-4 flex flex-col items-start gap-3 text-sm">
                  <a href="mailto:jan.perhac@tuke.sk" className="inline-flex items-center gap-2 font-medium text-primary-700 hover:underline dark:text-primary-300">
                    <Mail size={16} aria-hidden="true" />jan.perhac@tuke.sk
                  </a>
                  <a href="https://kpi.fei.tuke.sk/en/person/jan-perhac" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-medium text-primary-700 hover:underline dark:text-primary-300">
                    Ján Perháč — university profile <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                  <MapPin size={18} className="shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                  Institution & address
                </h3>
                <address className="not-italic text-sm leading-relaxed">
                  Department of Computers and Informatics<br />
                  Faculty of Electrical Engineering and Informatics<br />
                  Technical University of Košice<br />
                  Letná 9, 042 00 Košice<br />
                  Slovak Republic
                </address>
              </div>
            </div>
          </AboutSection>

          <AboutSection id="research" title="Research & presentations" icon={Presentation}>
            <div className="rounded-lg bg-primary-50 p-5 dark:bg-primary-900/20">
              <p className="mb-2 text-sm font-semibold text-primary-700 dark:text-primary-300">ThEdu’26 · FLoC 2026</p>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Rocq Game: A Gamified Web-Based Environment for Interactive Learning with a Proof Assistant
              </h3>
              <p className="mt-3">
                Presented at{' '}
                <a href="https://theduworkshop.github.io/ThEduWebSite/#thedu26-presentation" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-700 hover:underline dark:text-primary-300">
                  16th International Workshop on <strong>Th</strong>eorem proving components for <strong>Edu</strong>cational software (ThEdu’26)<span className="sr-only"> (opens in a new tab)</span>
                </a>, held alongside{' '}
                <a href="https://www.floc26.org/" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-700 hover:underline dark:text-primary-300">
                  The Federated Logic Conference 2026 (FLoC 2026)<span className="sr-only"> (opens in a new tab)</span>
                </a>{' '}
                in Lisbon, Portugal, on 19 July 2026.
              </p>
              <p className="mt-3 text-sm"><strong className="text-gray-900 dark:text-gray-100">Presentation authors:</strong> Khaled Suliman, Ján Perháč, and Samuel Novotný, Technical University of Košice.</p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-sm">
                <a href="https://theduworkshop.github.io/ThEduWebSite/#thedu26-presentation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:underline dark:text-primary-300">
                  Workshop website <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                </a>
                <a href="https://www.floc26.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:underline dark:text-primary-300">
                  Conference website <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                </a>
                <a href="https://theduworkshop.github.io/ThEduWebSite/#thedu26-program" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:underline dark:text-primary-300">
                  Workshop program <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                </a>
                <a href="https://theduworkshop.github.io/ThEduWebSite/slides/ThEdu26-slides-Perh%C3%A1%C4%8D.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:underline dark:text-primary-300">
                  Presentation slides (PDF) <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                </a>
              </div>
            </div>
          </AboutSection>

          <AboutSection id="technology" title="Built with" icon={Code2}>
            <p>Rocq Game brings together a proof engine, an interactive editor, and learning content organized into worlds and levels. Proof processing takes place in the browser; saved progress can be exported as JSON.</p>
            <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {technologies.map(([name, description]) => (
                <div key={name} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <dt className="mb-2 font-semibold text-gray-900 dark:text-gray-100">{name}</dt>
                  <dd className="text-sm">{description}</dd>
                </div>
              ))}
            </dl>
          </AboutSection>

          <AboutSection id="acknowledgment" title="Acknowledgment" icon={HeartHandshake}>
            <p>
              The authors gratefully acknowledge the support of the Cultural and Educational Grant Agency (KEGA)
              of the Ministry of Education, Research, Development and Youth of the Slovak Republic
              for the development of Rocq Game under project No. <strong className="text-gray-900 dark:text-gray-100">052TUKE-4/2025</strong>,{' '}
              <em>Modern Approaches in Educating IT Professionals in the Field of Type Theory</em>.
            </p>
            <p className="text-sm">
              The project runs from 2025 to 2027 at the Department of Computers and Informatics,
              Faculty of Electrical Engineering and Informatics, Technical University of Košice.
            </p>
            <p className="text-sm">
              <strong className="text-gray-900 dark:text-gray-100">Principal Investigator:</strong> Ing. Ján Perháč, PhD.
            </p>
            <a href="https://www.portalvs.sk/sk/prehlad-projektov/kega/16363" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline dark:text-primary-300">
              KEGA project 052TUKE-4/2025 <ExternalLink size={15} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
            </a>
            <div className="flex flex-wrap items-center gap-5 border-t border-gray-200 pt-5 dark:border-gray-700">
              <InstitutionLogo institution="fei" />
              <InstitutionLogo institution="minedu" />
            </div>
          </AboutSection>

          <AboutSection id="license" title="License & credits" icon={Scale}>
            <p>Rocq Game is available under the <a href="https://opensource.org/license/mit" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-700 underline underline-offset-2 dark:text-primary-300">MIT license<span className="sr-only"> (opens in a new tab)</span></a>.</p>
            <p className="text-sm">The project builds on the work of the Rocq, jsCoq, CodeMirror, and wider open-source communities. Third-party components retain their respective licenses.</p>
          </AboutSection>
          <div className="flex justify-end py-2 text-sm">
            <a href="#" className="text-gray-600 hover:underline dark:text-gray-300">Back to top</a>
          </div>
        </div>
      </div>
    </div>
  );
}
